/**
 * Whose Tixologi token the client sends right now.
 *
 * The native show surface calls the Tixologi API through one module-level axios instance
 * (`src/client.ts`), but a bearer token belongs to a venue page or a comedian: the scope
 * `TixologiScopeProvider` (`src/hooks/scope`) registers here. The client reads the current
 * scope when a request starts and keeps it on that request, so the request's retry never
 * borrows another venue's token.
 *
 * React-free, so the client and any non-React caller share it.
 */

/** What a scope owner registers. */
export type TixologiScopeSource = {
  /** The venue page id, or the comedian id when `isComedian`. */
  scopeId: string;
  isComedian: boolean;
  /** The latest token, or null while there is none. Read on every request. */
  getToken(): string | null;
  /** Logs in again; resolves the new token, or null when that fails. */
  refresh(): Promise<string | null>;
};

/** A registered scope: its source, with `refresh` shared by concurrent callers. */
export type RegisteredTixologiScope = Readonly<TixologiScopeSource>;

let current: RegisteredTixologiScope | null = null;

/**
 * Wraps a task so concurrent callers share one run; a call after it settles starts the
 * next. A run that throws resolves null.
 *
 * This is what makes a token refresh safe to call from several places at once: two
 * overlapping react-query `refetch()` calls cancel the first, whose caller then resolves
 * with the old data (packages/data useTixologiTokenRefresher has that race).
 */
export function singleFlight<T>(
  run: () => Promise<T>
): () => Promise<T | null> {
  let inFlight: Promise<T | null> | null = null;

  return () => {
    inFlight ??= Promise.resolve()
      .then(run)
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };
}

/**
 * Makes `source` the scope new requests are sent for, and returns its unregister. The
 * unregister clears the scope only while it is still the current one, so an outgoing
 * provider's cleanup can never unset the scope that replaced it.
 */
export function registerTixologiScope(source: TixologiScopeSource): () => void {
  const scope: RegisteredTixologiScope = {
    scopeId: source.scopeId,
    isComedian: source.isComedian,
    getToken: () => source.getToken(),
    refresh: singleFlight(() => source.refresh()),
  };
  current = scope;

  return () => {
    if (current === scope) current = null;
  };
}

/** The scope new requests are sent for, or null when none is registered. */
export function getCurrentTixologiScope(): RegisteredTixologiScope | null {
  return current;
}
